import PaginationNav from "src/components/actionDashboard/paginationNav";
import { FC, Fragment, useEffect, useState } from "react";
import { sp_treatments_data, SpecialityPharmacyTypeProps } from "./types";
import { QueryParamsState } from "src/components/manufacturer/caseListing";
import PharmacyTableHeader from "src/components/actionDashboard/specialityPharmacy/pharamcayTableHeader";
import NoDataFound from "src/components/noDataFoundComponent";
import pharamacyTableBody from "src/components/actionDashboard/specialityPharmacy/pharamacyTableBody";
import PharamacyTableBody from "src/components/actionDashboard/specialityPharmacy/pharamacyTableBody";
import { SPECIALITY_PHARMACY } from "src/constants/todos";

const SpecialityPharmacy: FC<SpecialityPharmacyTypeProps> = ({ tableData, currentPage, onPageChange, refreshListing }) => {
  const [queryParam, setQueryParam] = useState<QueryParamsState>({
    direction: "desc",
    sort_by: "bi_case_id",
  });

  const [sortedData, setSortedData] = useState<sp_treatments_data[]>([]);

  // Initialize sortedData whenever new data comes from props
  useEffect(() => {
    if (tableData?.sp_treatments_data) {
      setSortedData([...tableData.sp_treatments_data]);
    }
  }, [tableData]);

  const changeIdOrder = (tableHeader: string) => {
    const newParams: QueryParamsState = {
      sort_by: tableHeader,
      direction:
        tableHeader === queryParam.sort_by
          ? queryParam.direction === "desc"
            ? "asc"
            : "desc"
          : "desc",
    };

    let sorted = [...(tableData?.sp_treatments_data || [])];

    switch (newParams.sort_by) {
      case "bi_case_id":
        sorted.sort((a, b) =>
          newParams.direction === "asc"
            ? Number(a.bi_case_id) - Number(b.bi_case_id)
            : Number(b.bi_case_id) - Number(a.bi_case_id)
        );
        break;

      case "patient_initials_dob":
        sorted.sort((a, b) =>
          newParams.direction === "asc"
            ? (a.patient_initials_dob || "").localeCompare(b.patient_initials_dob || "")
            : (b.patient_initials_dob || "").localeCompare(a.patient_initials_dob || "")
        );
        break;

      case "drug_name":
        sorted.sort((a, b) =>
          newParams.direction === "asc"
            ? (a.drug_name || "").localeCompare(b.drug_name || "")
            : (b.drug_name || "").localeCompare(a.drug_name || "")
        );
        break;

      case "patient_name":
        sorted.sort((a, b) =>
          newParams.direction === "asc"
            ? (a.patient_name || "").localeCompare(b.patient_name || "")
            : (b.patient_name || "").localeCompare(a.patient_name || "")
        );
        break;

       case "provider":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.provider || "").localeCompare(b.provider || "")
             : (b.provider || "").localeCompare(a.provider || "")
         );
         break;

       case "sp_status":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.sp_status || "").localeCompare(b.sp_status || "")
             : (b.sp_status || "").localeCompare(a.sp_status || "")
         );
         break;

       case "sp_substatus":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.sp_substatus || "").localeCompare(b.sp_substatus || "")
             : (b.sp_substatus || "").localeCompare(a.sp_substatus || "")
         );
         break;

       case "payer":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.payer || "").localeCompare(b.payer || "")
             : (b.payer || "").localeCompare(a.payer || "")
         );
         break;

       case "insurance_type":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.insurance_type || "").localeCompare(b.insurance_type || "")
             : (b.insurance_type || "").localeCompare(a.insurance_type || "")
         );
         break;

       case "review_type":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.review_type || "").localeCompare(b.review_type || "")
             : (b.review_type || "").localeCompare(a.review_type || "")
         );
         break;

       case "qty":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? Number(a.qty ?? 0) - Number(b.qty ?? 0)
             : Number(b.qty ?? 0) - Number(a.qty ?? 0)
         );
         break;

       case "refills":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? Number(a.refills ?? 0) - Number(b.refills ?? 0)
             : Number(b.refills ?? 0) - Number(a.refills ?? 0)
         );
         break;

       case "fill_number":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? Number(a.fill_number ?? 0) - Number(b.fill_number ?? 0)
             : Number(b.fill_number ?? 0) - Number(a.fill_number ?? 0)
         );
         break;

       case "qty_transferred":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? Number(a.qty_transferred ?? 0) - Number(b.qty_transferred ?? 0)
             : Number(b.qty_transferred ?? 0) - Number(a.qty_transferred ?? 0)
         );
         break;

       case "qty_dispensed":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? Number(a.qty_dispensed ?? 0) - Number(b.qty_dispensed ?? 0)
             : Number(b.qty_dispensed ?? 0) - Number(a.qty_dispensed ?? 0)
         );
         break;

       case "oop_cost": {
         const oopCostValue = (cost: string | null | undefined) =>
           parseFloat(String(cost ?? "0").replace(/[^0-9.-]/g, "")) || 0;
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? oopCostValue(a.oop_cost) - oopCostValue(b.oop_cost)
             : oopCostValue(b.oop_cost) - oopCostValue(a.oop_cost)
         );
         break;
       }

       case "sp_transferred_to":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.sp_transferred_to || "").localeCompare(b.sp_transferred_to || "")
             : (b.sp_transferred_to || "").localeCompare(a.sp_transferred_to || "")
         );
         break;

       case "date_transferred":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.date_transferred || "").localeCompare(b.date_transferred || "")
             : (b.date_transferred || "").localeCompare(a.date_transferred || "")
         );
         break;

       case "pending_shipment_date":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? new Date(a.pending_shipment_date || 0).getTime() - new Date(b.pending_shipment_date || 0).getTime()
             : new Date(b.pending_shipment_date || 0).getTime() - new Date(a.pending_shipment_date || 0).getTime()
         );
         break;

       case "date_shipped":
         sorted.sort((a, b) =>
           newParams.direction === "asc"
             ? (a.date_shipped || "").localeCompare(b.date_shipped || "")
             : (b.date_shipped || "").localeCompare(a.date_shipped || "")
         );
         break;
          case "provider_treatment_status":
           sorted.sort((a, b) =>
             newParams.direction === "asc"
               ? (a.provider_treatment_status || "").localeCompare(b.provider_treatment_status || "")
               : (b.provider_treatment_status || "").localeCompare(a.provider_treatment_status || "")
           );
           break;
   
         case "territory":
           sorted.sort((a, b) =>
             newParams.direction === "asc"
               ? (a.territory || "").localeCompare(b.territory || "")
               : (b.territory || "").localeCompare(a.territory || "")
           );
           break;
   
         case "state":
           sorted.sort((a, b) =>
             newParams.direction === "asc"
               ? (a.state || "").localeCompare(b.state || "")
               : (b.state || "").localeCompare(a.state || "")
           );
           break;

      default:
        break;
    }
    setSortedData(sorted);
    setQueryParam(newParams);
  };
  return (
    <Fragment>

      <div className="table-responsive table-responsive-sm table-responsive-md table-responsive-lg table-responsive-xl flex-fill">
        <table className="table table-striped table-sm m-0" style={{minHeight:'300px'}}>
          
          <PharmacyTableHeader  queryParam={queryParam} changeIdOrder={changeIdOrder} tableHeader={SPECIALITY_PHARMACY} />
          {sortedData && sortedData.length > 0 ?
            <PharamacyTableBody
              data={sortedData}
              refreshListing={refreshListing}
            /> :
            <NoDataFound />
          }
        </table>
      </div>
      {tableData?.sp_treatments_data?.length > 0 && tableData.page_count > 1 && (
        <PaginationNav currentPage={currentPage} pageCount={tableData.page_count} onPageChange={onPageChange} />
      )}
    </Fragment>
  );
};

export default SpecialityPharmacy;
 